/** 편집 가능한 테이블 셀 — TableCell을 감싸고 편집 로직만 추가 */

import type { PeriodType } from "@/apis/compare/types";
import type { CellChangeOrigin } from "@/utils/ixd-cell-model";
import {
  applyUnit,
  formatMonetaryTextForEditing,
  parseNumericValue,
} from "@/utils/ixd-editor/unit-conversion";
import {
  formatStoredValueForEditing,
  getEditableInputModeForSchema,
  getEditableMaxLengthForSchema,
  getIxdSchemaValueKind,
  isNumericSchemaKind,
  normalizeEditedCellValue,
  sanitizeEditableInputElement,
  sanitizeEditableInputForSchema,
  skipSeparatorOnDelete,
} from "@/utils/ixd-editor/value-validation";
import {
  buildValueCellDataset,
  formatCellForDisplay,
  formatExpectedAutoSumValueForDisplay,
} from "@/utils/ixd-editor/cell-display";
import { cn } from "@/lib/utils";
import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import TableCell, { type TableCellColumn } from "./table-cell";
import { cellFootnote } from "./cell-footnote";
import { useIxdPreviewConfig } from "./preview/ixd-preview-config-context";

export interface EditableTableCellProps {
  column: TableCellColumn;
  periodType: PeriodType;
  /** DOM 유니크 ID */
  uniqueId?: string;
  /** 연동 계정 인덱스가 사용하는 원본 보고서 위치 UID */
  identityLocationUid?: string;
  /** tagged value metadata를 우선 반영한 유효 schema type */
  schemaType?: string | null;
  referenceBalance?: "DEBIT" | "CREDIT" | null;
  labelMap?: Map<string, string>;
  editedValue?: string;
  /** 사용자가 직접 수정하거나 붙여넣은 pending 값인지 여부 */
  showManualEditMarker?: boolean;
  /** pending 값의 출처 — 셀 하단 문구가 "값 반영"만 가려내는 데 쓴다 */
  changeOrigin?: CellChangeOrigin;
  /** 저장/편집 경계에서 쓰는 canonical cell id */
  editCellId?: string;
  cellIdentityKey?: string;
  relatedCellIdentityCount?: number;
  onEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
  onEditStart?: () => void;
  /** 단위 변환 제수 (1=원, 1000=천원, 1000000=백만원) */
  unitDivisor?: number;
  /** 편집 입력창에 원본 원값 표시 여부 */
  showRawValueInEdit?: boolean;
  /** 행 헤더가 negated인 경우 표기 값의 부호를 반전 */
  negated?: boolean;
  /** 행 헤더 decimalDigits — 입력 값 소수점 아래 자릿수 절삭 제약 */
  decimalDigits?: number | null;
  /** 셀 선택 마우스 이벤트 핸들러 */
  onCellMouseDown?: (cellId: string, event: React.MouseEvent) => void;
  onCellMouseEnter?: (event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
}

const areColumnsEqual = (
  previous: TableCellColumn,
  next: TableCellColumn,
): boolean =>
  previous === next ||
  (previous.id === next.id &&
    previous.tagName === next.tagName &&
    previous.autoSumValueDifferent === next.autoSumValueDifferent &&
    previous.autoSumValueSame === next.autoSumValueSame &&
    previous.expectedAutoSumValue === next.expectedAutoSumValue &&
    previous.scope === next.scope &&
    previous.text === next.text &&
    previous.rowspan === next.rowspan &&
    previous.colspan === next.colspan &&
    previous.attributes.extended === next.attributes.extended &&
    previous.attributes.contextKey === next.attributes.contextKey &&
    previous.negated === next.negated &&
    previous.balance === next.balance &&
    previous.schemaType === next.schemaType &&
    previous.decimalDigits === next.decimalDigits &&
    previous.isAxisTotal === next.isAxisTotal);

const areEditableTableCellPropsEqual = (
  previous: EditableTableCellProps,
  next: EditableTableCellProps,
): boolean => {
  if (!areColumnsEqual(previous.column, next.column)) return false;
  if (previous.periodType !== next.periodType) return false;
  if (previous.uniqueId !== next.uniqueId) return false;
  if (previous.identityLocationUid !== next.identityLocationUid) return false;
  if (previous.schemaType !== next.schemaType) return false;
  if (previous.referenceBalance !== next.referenceBalance) return false;
  if (previous.labelMap !== next.labelMap) return false;

  if (previous.column.tagName === "th" && next.column.tagName === "th") {
    return true;
  }

  return (
    previous.editedValue === next.editedValue &&
    previous.showManualEditMarker === next.showManualEditMarker &&
    previous.changeOrigin === next.changeOrigin &&
    previous.editCellId === next.editCellId &&
    previous.cellIdentityKey === next.cellIdentityKey &&
    previous.relatedCellIdentityCount === next.relatedCellIdentityCount &&
    previous.onEdit === next.onEdit &&
    previous.onEditStart === next.onEditStart &&
    previous.unitDivisor === next.unitDivisor &&
    previous.showRawValueInEdit === next.showRawValueInEdit &&
    previous.negated === next.negated &&
    previous.decimalDigits === next.decimalDigits &&
    previous.onCellMouseDown === next.onCellMouseDown &&
    previous.onCellMouseEnter === next.onCellMouseEnter &&
    previous.onCellMouseUp === next.onCellMouseUp
  );
};

const EditableTableCell = memo(
  ({
    column,
    periodType,
    uniqueId,
    identityLocationUid,
    schemaType,
    referenceBalance,
    labelMap,
    editedValue,
    showManualEditMarker,
    changeOrigin,
    editCellId,
    cellIdentityKey,
    relatedCellIdentityCount,
    onEdit,
    onEditStart,
    unitDivisor = 1,
    showRawValueInEdit = false,
    negated = false,
    decimalDigits = null,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
  }: EditableTableCellProps) => {
    const { cellNotices, showCellFootnotes } = useIxdPreviewConfig();
    const Tag = column.tagName;
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState("");
    const [editStartValue, setEditStartValue] = useState("");
    const [optimisticWonValue, setOptimisticWonValue] = useState<
      string | undefined
    >(undefined);
    const editorRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const isConfirmingRef = useRef(false);

    const isSelectableCell = Tag === "td" && !!uniqueId;
    const effectiveCellId = editCellId ?? column.id;
    const isEditableCell =
      isSelectableCell && !!onEdit && !!effectiveCellId && column.editable;

    // prop이 도착하면 optimistic 값 해제
    useEffect(() => {
      if (
        optimisticWonValue !== undefined &&
        editedValue === optimisticWonValue
      ) {
        setOptimisticWonValue(undefined);
      }
    }, [editedValue, optimisticWonValue]);

    const effectiveEditedValue = optimisticWonValue ?? editedValue;
    const hasBeenEdited = effectiveEditedValue !== undefined;
    const effectiveSchemaType = schemaType ?? column.schemaType;
    const valueKind = getIxdSchemaValueKind(effectiveSchemaType);
    const isMoney = valueKind === "MONETARY";
    const alignRight = isNumericSchemaKind(effectiveSchemaType);

    // column.text는 원값 ("123456789")으로 들어옴
    const rawText = hasBeenEdited ? effectiveEditedValue : column.text;
    const rawNum = parseNumericValue(rawText);
    const isNumeric = rawNum !== null;

    // 표기 값: 통일된 헬퍼로 산출 (read-only TableCell도 같은 헬퍼 사용)
    const { displayText } = formatCellForDisplay({
      rawText,
      schemaType: effectiveSchemaType,
      unitDivisor,
      negated,
      decimalDigits,
    });
    const expectedAutoSumDisplayText = formatExpectedAutoSumValueForDisplay(
      column.expectedAutoSumValue,
    );

    useEffect(() => {
      if (isEditing && editorRef.current) {
        editorRef.current.focus();
        editorRef.current.select();
      }
    }, [isEditing]);

    useLayoutEffect(() => {
      const editor = editorRef.current;
      if (!isEditing || !(editor instanceof HTMLTextAreaElement)) return;
      editor.style.height = "auto";
      editor.style.height = `${editor.scrollHeight}px`;
    }, [isEditing, localValue]);

    // Hook 호출 순서를 유지한 뒤 th는 편집 불가 셀로 위임한다.
    if (Tag === "th") {
      return (
        <TableCell
          column={column}
          periodType={periodType}
          referenceBalance={referenceBalance}
        />
      );
    }

    const handleStartEdit = () => {
      if (!isEditableCell) return;
      onEditStart?.();
      // 편집 입력은 단위 적용 값 그대로 (negated 부호 반전 없이).
      // 표시와 같은 문자열 십진 경로로 산출해 셀 표시 자릿수(decimalDigits)와
      // 일치시킨다 — Number 경로는 정수 반올림이라 소수 자릿수가 유실된다.
      const initial =
        isMoney && isNumeric
          ? ((showRawValueInEdit
              ? formatMonetaryTextForEditing({ rawText, divisor: 1 })
              : formatMonetaryTextForEditing({
                  rawText,
                  divisor: unitDivisor,
                  decimalDigits,
                })) ??
            String(
              showRawValueInEdit ? rawNum : applyUnit(rawNum, unitDivisor),
            ))
          : formatStoredValueForEditing(
              effectiveSchemaType,
              rawText,
              decimalDigits,
            );
      setLocalValue(initial);
      setEditStartValue(initial);
      setIsEditing(true);
    };

    const finishConfirmEdit = () => {
      setIsEditing(false);
      window.setTimeout(() => {
        isConfirmingRef.current = false;
      }, 0);
    };

    const handleConfirmEdit = () => {
      if (isConfirmingRef.current) return;
      isConfirmingRef.current = true;
      if (!onEdit || !effectiveCellId) {
        finishConfirmEdit();
        return;
      }
      if (localValue === editStartValue) {
        finishConfirmEdit();
        return;
      }

      const normalized = normalizeEditedCellValue({
        schemaType: effectiveSchemaType,
        inputValue: localValue,
        unitDivisor,
        displayValueMode: showRawValueInEdit ? "raw" : "unit-applied",
        displayUsesNegatedSign: false,
        negated,
        decimalDigits,
      });

      if (!normalized.ok) {
        console.error("[DEBUG] IXD 셀 편집 검증 실패", {
          cellId: effectiveCellId,
          schemaType: effectiveSchemaType,
          inputValue: localValue,
          currentRawText: rawText,
          unitDivisor,
          displayValueMode: showRawValueInEdit ? "raw" : "unit-applied",
          negated,
          decimalDigits,
          errorMessage: normalized.errorMessage,
        });
        toast.error(normalized.errorMessage);
        finishConfirmEdit();
        return;
      }

      if (normalized.storedValue === rawText) {
        finishConfirmEdit();
        return;
      }

      setOptimisticWonValue(normalized.storedValue);
      onEdit(
        effectiveCellId,
        normalized.storedValue,
        cellIdentityKey,
        identityLocationUid,
      );

      finishConfirmEdit();
    };

    const handleCancelEdit = () => {
      setIsEditing(false);
    };

    const handleApplyExpectedAutoSum = () => {
      if (!onEdit || !effectiveCellId || !expectedAutoSumDisplayText) return;

      const normalized = normalizeEditedCellValue({
        schemaType: effectiveSchemaType,
        inputValue: expectedAutoSumDisplayText,
        unitDivisor,
        displayValueMode: "unit-applied",
        displayUsesNegatedSign: false,
        negated,
        decimalDigits,
      });

      if (!normalized.ok) {
        console.error("[DEBUG] IXD 예상 자동합 적용 검증 실패", {
          cellId: effectiveCellId,
          schemaType: effectiveSchemaType,
          inputValue: expectedAutoSumDisplayText,
          unitDivisor,
          negated,
          decimalDigits,
          errorMessage: normalized.errorMessage,
        });
        toast.error(normalized.errorMessage);
        return;
      }

      if (normalized.storedValue === rawText) {
        toast.info("이미 예상 자동합과 같은 값입니다.");
        return;
      }

      onEditStart?.();
      setOptimisticWonValue(normalized.storedValue);
      onEdit(
        effectiveCellId,
        normalized.storedValue,
        cellIdentityKey,
        identityLocationUid,
      );
      setIsEditing(false);
      toast.success("예상 자동합을 적용했습니다.");
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleConfirmEdit();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      } else {
        skipSeparatorOnDelete(effectiveSchemaType, e);
      }
    };

    const handleTextareaKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        handleConfirmEdit();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    };

    const usesMultilineEditor =
      valueKind === "TEXT" ||
      displayText.includes("\n") ||
      rawText.includes("\n");

    const editorSizerText = displayText || localValue || " ";
    const editorClassName = cn(
      "col-start-1 row-start-1 w-full min-w-0 box-border bg-background-base border border-primary-base rounded px-1 py-0 caption-2-medium text-label-normal outline-none",
      alignRight && "text-right",
    );

    // 편집 중이면 editor, 아니면 기본 콘텐츠
    const cellContent = isEditing ? (
      <div className="grid min-w-0">
        <span
          aria-hidden
          className="invisible col-start-1 row-start-1 block min-h-[1.375rem] whitespace-pre-wrap [overflow-wrap:anywhere]"
        >
          {editorSizerText}
        </span>
        {usesMultilineEditor ? (
          <textarea
            ref={editorRef as React.RefObject<HTMLTextAreaElement>}
            value={localValue}
            maxLength={getEditableMaxLengthForSchema(effectiveSchemaType)}
            onChange={(e) =>
              setLocalValue(
                sanitizeEditableInputForSchema(
                  effectiveSchemaType,
                  e.target.value,
                ),
              )
            }
            onBlur={handleConfirmEdit}
            onKeyDown={handleTextareaKeyDown}
            title="셀 값 편집"
            rows={1}
            className={cn(
              editorClassName,
              "min-h-[1.375rem] resize-none overflow-hidden whitespace-pre-wrap [overflow-wrap:anywhere]",
            )}
          />
        ) : (
          <input
            ref={editorRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={localValue}
            inputMode={getEditableInputModeForSchema(effectiveSchemaType)}
            maxLength={getEditableMaxLengthForSchema(effectiveSchemaType)}
            onChange={(e) =>
              setLocalValue(
                sanitizeEditableInputElement(effectiveSchemaType, e.target),
              )
            }
            onBlur={handleConfirmEdit}
            onKeyDown={handleInputKeyDown}
            title="셀 값 편집"
            className={editorClassName}
          />
        )}
      </div>
    ) : (
      // display:flex 라 부모의 text-right 가 안 먹는다 — justify 로 맞춘다
      <span
        className={cn("flex items-center gap-1", alignRight && "justify-end")}
      >
        {displayText}
      </span>
    );

    // 편집 전용 className
    const editClassName = cn(
      hasBeenEdited && !isEditing && "bg-amber-50",
      isEditing && "bg-amber-50 ring-1 ring-inset ring-amber-300",
      isSelectableCell &&
        !isEditing &&
        (hasBeenEdited
          ? "cursor-pointer hover:bg-amber-100 select-none"
          : "cursor-pointer hover:bg-slate-50 select-none"),
    );

    // 셀 선택 이벤트 (readonly도 선택은 가능)
    const handleMouseDown = (e: React.MouseEvent) => {
      if (!isSelectableCell || isEditing || !onCellMouseDown || !uniqueId)
        return;
      onCellMouseDown(uniqueId, e);
    };
    const handleMouseEnter = (e: React.MouseEvent) => {
      if (!isSelectableCell || isEditing || !onCellMouseEnter) return;
      onCellMouseEnter(e);
    };
    const handleMouseUp = () => {
      if (!isSelectableCell || isEditing || !onCellMouseUp) return;
      onCellMouseUp();
    };

    // 값이 들어가지 않은 이유는 그 셀에서 바로 보이는 것이 가장 직관적이다.
    // 편집 가능 셀도 같은 표시를 받아야 하므로 여기서도 config를 읽는다.
    const cellNotice = cellIdentityKey
      ? cellNotices?.get(cellIdentityKey)
      : undefined;
    const tdProps = {
      ...buildValueCellDataset({
        cellId: effectiveCellId,
        sourceCellId: column.id,
        rawText,
        displayText,
        identityKey: cellIdentityKey,
        schemaType: effectiveSchemaType,
        unitDivisor,
        negated,
        decimalDigits,
        readonly: !column.editable,
        edited: hasBeenEdited,
        axisTotal: column.isAxisTotal,
      }),
      "data-cell-identity-location-uid": identityLocationUid,
      onMouseDown: handleMouseDown,
      onMouseEnter: handleMouseEnter,
      onMouseUp: handleMouseUp,
      onDoubleClick: isEditableCell && !isEditing ? handleStartEdit : undefined,
      ...(cellNotice && cellIdentityKey
        ? {
            "data-cell-notice": cellNotice.tone,
            title: cellNotice.description,
          }
        : {}),
    } satisfies React.TdHTMLAttributes<HTMLTableCellElement> &
      Record<string, unknown>;

    return (
      <TableCell
        column={column}
        periodType={periodType}
        uniqueId={uniqueId}
        referenceBalance={referenceBalance}
        labelMap={labelMap}
        relatedCellIdentityCount={relatedCellIdentityCount}
        showManualEditMarker={showManualEditMarker}
        showNoticeMarker={cellNotice?.tone === "blocked"}
        footnote={
          showCellFootnotes
            ? cellFootnote({
                hasNotice: !!cellNotice,
                linkedCount: relatedCellIdentityCount,
                changeOrigin,
                hasValue: !!displayText,
                hadValue: !!column.text,
              })
            : undefined
        }
        expectedAutoSumDisplayText={expectedAutoSumDisplayText}
        onApplyExpectedAutoSum={
          isEditableCell && expectedAutoSumDisplayText
            ? handleApplyExpectedAutoSum
            : undefined
        }
        displayText={isEditing ? undefined : displayText}
        alignRight={alignRight}
        cellContent={cellContent}
        cellClassName={cn(
          editClassName,
          // 확인 알림은 편집 대기 강조보다 우선한다 — 값 반영으로 값이 들어간 셀도
          // 알림 대상이므로, 커서를 올렸을 때도 보라색을 유지한다.
          cellNotice?.tone === "conflict" &&
            "bg-purple-100 hover:bg-purple-200",
        )}
        tdProps={tdProps}
      />
    );
  },
  areEditableTableCellPropsEqual,
);

EditableTableCell.displayName = "EditableTableCell";

export default EditableTableCell;
